import React from "react";
import { compose, withAppContext, withActions } from "containers/HOC";
import { Link } from "react-router-dom";
import { push } from "connected-react-router";
import * as utils from "common/utils";
import { useModal } from "common/effects";
import { useAuthContext, useOnline, useRefData } from "common/hooks";
import Card from "components/new/Card";
import CardHeader from "components/new/CardHeader";
import CardHeaderColumn from "components/new/CardHeaderColumn";
import CardHeaderField from "components/new/CardHeaderField";
import CardBody from "components/new/CardBody";
import CardActions from "components/new/CardActions";
import CardActionStatus from "components/new/CardActionStatus";
import CardActionsLeft from "components/new/CardActionsLeft";
import CardActionsRight from "components/new/CardActionsRight";
import ActionLink from "components/ActionLink";
import Panel from "components/Panel/Panel";
import PanelBody from "components/Panel/PanelBody";
import { modalType } from "components/ModalConductor";
import AnalysisResultsWidget from "components/ResultsWidgets/AnalysisResultsWidget";
import { modal as reduxModal, confirm } from "containers/App/_actions";
import { grantBudgetAccess, deleteBudget, downloadBudget, addBudgetToStudentGroup } from "containers/Budget/_actions";
import PublicationCard from "containers/Publications/PublicationCard";
import { getStudents, getAccount } from "../AccountDetails/_actions";
import RenameAnalysisModal from "./Modals/RenameAnalysisModal";
import AnalysisAuditLogModal from "./Modals/AnalysisAuditLogModal";
import CreateAnalysisModal from "./Modals/CreateAnalysisModal";
import ReplaceAnalysisModal from "./Modals/ReplaceAnalysisModal";
import SimplifyAnalysisModal from "./Modals/SimplifyAnalysisModal";
import CompareAnalysesModal from "./Modals/CompareAnalysesModal";
import { useAnalysisCommentsModal } from "containers/FarmHome/AnalysisCommentsModal";
import { PublishAnalysisActionLink, isPublishingToMPIWholeFarmDataProject } from "containers/Publications";
import { navigate } from "containers/App/_actions";
import { useDispatch } from "react-redux";
import { useFeatureTracker, FEATURES } from "components/FeatureTracker/FeatureTracker";

const AnalysisCard = (props) => {
    const { farm, offlineBudget, push, reduxModal, confirm, grantBudgetAccess, addBudgetToStudentGroup, deleteBudget, downloadBudget, getStudents, parentTitle = "", showVisibility = false, getAccount } = props;
    const { budget, isDownloaded } = offlineBudget;
    let { isPlaceholder } = offlineBudget;
    const [analysisCommentsModal, openAnalysisCommentsModal] = useAnalysisCommentsModal(budget.farmId, budget.id);
    const online = useOnline();
    const authContext = useAuthContext();
    const refData = useRefData();
    const dispatch = useDispatch();
    const featureTracker = useFeatureTracker(FEATURES.FARM_IMPACT_REPORT, undefined, farm);
    const linkFeatureTracker = useFeatureTracker(FEATURES.ANALYSIS_LINKS_LIST, undefined, farm);

    const { openModal, getModal } = useModal();

    const _addToDataset = () => {
        const viewModel = {
            analysis: budget,
        };
        reduxModal("AddToDatasetModal", null, viewModel);
    };

    const _viewAnalysis = () => {
        linkFeatureTracker.track("View analysis");
        push(`/app/farm/${farm.id}/analysis/${budget.id}`);
    };

    const _viewComments = () => {
        openAnalysisCommentsModal();
    };

    const _viewAuditLog = () => {
        const viewModel = {
            farm,
            analysis: budget,
        };
        openModal("AnalysisAuditLogModal", viewModel);
    };

    const _renameAnalysis = () => {
        const viewModel = {
            farm: farm,
            analysisId: budget.id,
        };
        openModal("RenameAnalysisModal", viewModel);
    };

    const _shareAnalysis = () => {
        const viewModel = {
            budgetId: budget.id,
            name: budget.name,
            visibility: budget.visibility,
        };

        const evalShareType = (options) => {
            if (options.visibility !== "Students") {
                grantBudgetAccess(budget, options);
            } else {
                addBudgetToStudentGroup(budget, options);
            }
        };

        getStudents();
        getAccount();

        reduxModal(modalType.SHARE_BUDGET, (options) => evalShareType(options), viewModel);
    };

    const _compareAnalyses = () => {
        featureTracker.track("Compare from analysis list");
        dispatch(navigate(`/app/farm/${farm.id}/analysis/${budget.id}/compare`));
    };

    const _simplifyAnalysis = () => {
        const viewModel = {
            farm,
            budget,
        };
        openModal("SimplifyAnalysisModal", viewModel);
    };

    const _copyAnalysis = () => {
        const viewModel = {
            farm,
            analysisId: budget.id,
            initialCreationType: "Copy",
        };
        openModal("CreateAnalysisModal", viewModel);
    };

    const _replaceAnalysis = () => {
        const viewModel = {
            farm,
            analysis: budget,
        };
        openModal("ReplaceAnalysisModal", viewModel);
    };

    const _deleteAnalysis = () => {
        confirm(`Are you sure you want to delete the "${budget.name}" analysis?`, () => deleteBudget(budget), false);
    };

    const publications = (farm.publications || []).filter((p) => p.budgetId === budget.id && (!isPublishingToMPIWholeFarmDataProject(p.accountId) || authContext.canPublishToMPIWholeFarmDataProject)).sort((a, b) => b.version - a.version);
    const hasBodyContent = refData.deploymentType !== "Science" && publications.length > 0;

    const guestReadOnly = budget.role === "Header";
    isPlaceholder = isPlaceholder || guestReadOnly;
    const hasFarmUpdateAccess = utils.canUpdateFarm(farm);
    const hasManageAnalysisAccess = utils.canManageAnalyses(farm);
    const hasAnalysisWriteAccess = budget.role === "Write";
    const canView = isPlaceholder ? false : online || isDownloaded;
    const isPrivateYearEnd = budget.type === "PrivateYearEnd";
    const educationProvider = authContext.accountType === "EducationProvider";
    const canShare = online && hasFarmUpdateAccess && hasAnalysisWriteAccess && !farm.studentGroup && (budget.visibility !== "Farm" || educationProvider) && authContext.accountType !== "Student" && !isPrivateYearEnd;
    const canAddDatasets = online && refData.deploymentType === "Science";
    const canDownload = online && authContext.isSystemAdmin;
    const canPublish = online && refData.deploymentType !== "Science" && budget.hasCompletedBlocks && (!farm.expired || (hasFarmUpdateAccess && authContext.canPublishWithoutSubscription)) && ((hasAnalysisWriteAccess && budget.type === "PrivateYearEnd") || (hasFarmUpdateAccess && ["YearEnd", "Predictive"].includes(budget.type)));
    const canViewComments = hasAnalysisWriteAccess;
    const canViewAuditLog = online && hasAnalysisWriteAccess;
    const canDelete = online && hasAnalysisWriteAccess && publications.length === 0;
    const canCopy = online && hasAnalysisWriteAccess && hasManageAnalysisAccess;
    const canReplace = online && hasFarmUpdateAccess && budget.type === "YearEnd" && (farm.offlineBudgets || []).length > 1 && budget.hasCompletedBlocks;
    const canCompare = online && budget.hasCompletedBlocks;
    const canRename = online && hasAnalysisWriteAccess && ["Predictive", "Scenario", "Account"].includes(budget.type);
    const canSimplify = process.env.REACT_APP_DISABLE_MERGE_BLOCKS !== "true" && online && budget.nLossPerHa;
    const warningMessages = (budget.messages || []).filter((m) => m.severity !== "Error" && !budget.suppressedMessageCodes.includes(m.code)).map((m) => m.code);
    const warnings = Array.from(new Set(warningMessages));
    const errorMessages = (budget.messages || []).filter((m) => m.severity === "Error").map((m) => m.code);
    const errors = Array.from(new Set(errorMessages));
    const defaultToOpen = isPlaceholder || publications.some((p) => p.status === "Returned");
    const showPreviousResults = !budget.previousNLossPerHa || budget.previousNLossPerHa === 0 ? false : budget.nLossPerHa === budget.previousNLossPerHa && budget.pLossPerHa === budget.previousPLossPerHa && budget.ghgkPerHa > 0 && budget.ghgkPerHa === budget.previousGHGKPerHa ? false : true;
    const previousMessage = !budget.previousNLossPerHa || budget.previousNLossPerHa === 0 ? `v${budget.previousModelVersion}: no results` : budget.nLossPerHa === budget.previousNLossPerHa && budget.pLossPerHa === budget.previousPLossPerHa && budget.ghgkPerHa > 0 && budget.ghgkPerHa === budget.previousGHGKPerHa ? `v${budget.previousModelVersion}: no change` : `v${budget.previousModelVersion}: `;
    const renameAnalysisModal = getModal("RenameAnalysisModal");
    const analysisAuditLogModal = getModal("AnalysisAuditLogModal");
    const createAnalysisModal = getModal("CreateAnalysisModal");
    const replaceAnalysisModal = getModal("ReplaceAnalysisModal");
    const simplifyAnalysisModal = getModal("SimplifyAnalysisModal");
    const compareAnalysesModal = getModal("CompareAnalysesModal");

    return (
        <>
            <Card collapsible open={defaultToOpen}>
                {({ collapsed, toggle }) => {
                    return (
                        <>
                            <CardHeader id={budget.id} name={parentTitle || budget.id} dataWidth={`md-100 ${canPublish || hasBodyContent ? "xl-30" : "xl-40"}`} title={budget.name} subTitle={budget.year} markerIcon={hasBodyContent ? <i className="icon icon-bookmarks" title="Published" /> : undefined} collapsed={collapsed} onTitleClick={canView && _viewAnalysis}>
                                {guestReadOnly && (
                                    <>
                                        {hasBodyContent && (
                                            <CardHeaderColumn dataWidth="md-15 xl-10">
                                                <CardHeaderField
                                                    label={`Publications (${publications.length})`}
                                                    value={
                                                        hasBodyContent ? (
                                                            <ActionLink id={`view-analysis-publications-${budget.id}`} className={collapsed ? "IconLink--arrow-down" : "IconLink--arrow-up"} onClick={toggle()}>
                                                                {collapsed ? "Show" : "Hide"}
                                                            </ActionLink>
                                                        ) : (
                                                            "-"
                                                        )
                                                    }
                                                />
                                            </CardHeaderColumn>
                                        )}
                                        <CardHeaderColumn>
                                            <CardHeaderField label="Access" value="You do not have access to view this analysis. You will need to request access or wait until your request is granted." />
                                        </CardHeaderColumn>
                                    </>
                                )}
                                {!guestReadOnly && (
                                    <>
                                        {(canPublish || hasBodyContent) && (
                                            <CardHeaderColumn dataWidth="md-15 xl-10">
                                                <CardHeaderField
                                                    label={`Publications (${publications.length})`}
                                                    value={
                                                        hasBodyContent ? (
                                                            <ActionLink id={`view-analysis-publications-${budget.id}`} className={collapsed ? "IconLink--arrow-down" : "IconLink--arrow-up"} onClick={toggle()}>
                                                                {collapsed ? "Show" : "Hide"}
                                                            </ActionLink>
                                                        ) : (
                                                            "-"
                                                        )
                                                    }
                                                />
                                            </CardHeaderColumn>
                                        )}
                                        <CardHeaderColumn dataWidth="md-15 xl-10">
                                            <CardHeaderField label="Created" value={budget.created ? utils.dateOnly(budget.created) : "-"} />
                                        </CardHeaderColumn>
                                        <CardHeaderColumn dataWidth="md-15 xl-10">
                                            <CardHeaderField label="Modified" value={budget.lastUpdated ? utils.dateOnly(budget.lastUpdated) : "-"} />
                                        </CardHeaderColumn>
                                        {showVisibility && (
                                            <CardHeaderColumn dataWidth="md-15 xl-10">
                                                <CardHeaderField label="Visibility" value={budget.visibility} />
                                            </CardHeaderColumn>
                                        )}
                                        {canAddDatasets && budget.datasetName && (
                                            <CardHeaderColumn dataWidth="md-15 xl-10">
                                                <CardHeaderField label="Dataset" value={budget.datasetName} />
                                            </CardHeaderColumn>
                                        )}
                                        {!isPlaceholder && (
                                            <CardHeaderColumn nutrientsLost>
                                                <AnalysisResultsWidget farm={farm} analysis={budget} perHaOnly />
                                            </CardHeaderColumn>
                                        )}
                                    </>
                                )}
                            </CardHeader>
                            <CardActions>
                                <CardActionsLeft>
                                    {online && !isPlaceholder && (
                                        <>
                                            {!budget.isDirty && <CardActionStatus className="icon icon-sync u-textSuccess" status="Synced" />}
                                            {budget.isDirty && budget.error && <CardActionStatus className="icon icon-sync u-textError" status="Sync error" />}
                                            {budget.isDirty && !budget.error && <CardActionStatus className="icon icon-sync u-textWarning" status="Sync pending" />}
                                            {warnings.length > 0 && <CardActionStatus className="icon icon-dot u-textWarning" status={`${warnings.length} attention`} />}
                                            {errors.length > 0 && <CardActionStatus className="icon icon-dot u-textError" status={`${errors.length} error(s)`} />}
                                            <CardActionStatus status={previousMessage} />
                                            {showPreviousResults && (
                                                <React.Fragment>
                                                    {budget.previousNLossPerHa ? <CardActionStatus className="icon icon-square N-color" status={`N/ha: ${budget.previousNLossPerHa}`} /> : ""}
                                                    {budget.previousPLossPerHa ? <CardActionStatus className="icon icon-square P-color" status={`P/ha: ${budget.previousPLossPerHa}`} /> : ""}
                                                    {budget.previousGHGKPerHa ? <CardActionStatus className="icon icon-square GHG-color" status={`GHG/ha: ${budget.previousGHGKPerHa}`} /> : ""}
                                                </React.Fragment>
                                            )}
                                        </>
                                    )}
                                    {!online && !canView && <CardActionStatus className="icon icon-cross-circle u-textError" status="Offline" />}
                                </CardActionsLeft>
                                <CardActionsRight>
                                    {canView && (
                                        <ul className="ActionsBar-links">
                                            {canRename && (
                                                <li>
                                                    <ActionLink id={`rename-analysis-${budget.id}`} className="IconLink--settings" onClick={_renameAnalysis}>
                                                        Rename
                                                    </ActionLink>
                                                </li>
                                            )}
                                            {canViewAuditLog && (
                                                <li>
                                                    <ActionLink id={`view-analysis-auditlog-${budget.id}`} className="IconLink--clock" onClick={_viewAuditLog}>
                                                        Audit log
                                                    </ActionLink>
                                                </li>
                                            )}
                                            {canViewComments && (
                                                <li>
                                                    <ActionLink id={`view-analysis-comments-${budget.id}`} className="IconLink--chat" onClick={_viewComments}>
                                                        Comments ({(budget.comments || []).length})
                                                    </ActionLink>
                                                </li>
                                            )}
                                            {canAddDatasets && (
                                                <li>
                                                    <ActionLink id={`set-dataset-${budget.id}`} className="IconLink--tick-circle" onClick={_addToDataset}>
                                                        {budget.datasetId ? "Update dataset" : "Add to dataset"}
                                                    </ActionLink>
                                                </li>
                                            )}
                                            {canShare && (
                                                <li>
                                                    <ActionLink id={`share-analysis-${budget.id}`} className="IconLink--share" onClick={_shareAnalysis}>
                                                        Share
                                                    </ActionLink>
                                                </li>
                                            )}
                                            {canPublish && (
                                                <li>
                                                    <PublishAnalysisActionLink farmId={budget.farmId} budgetId={budget.id} />
                                                </li>
                                            )}
                                            {canDelete && (
                                                <li>
                                                    <ActionLink id={`delete-analysis-${budget.id}`} className="IconLink--trash" onClick={_deleteAnalysis}>
                                                        Delete
                                                    </ActionLink>
                                                </li>
                                            )}
                                            {canCopy && (
                                                <li>
                                                    <ActionLink id={`copy-analysis-${budget.id}`} className="IconLink--copy" onClick={_copyAnalysis}>
                                                        Copy
                                                    </ActionLink>
                                                </li>
                                            )}
                                            {canReplace && (
                                                <li>
                                                    <ActionLink id={`replace-analysis-${budget.id}`} className="IconLink--replace" onClick={_replaceAnalysis}>
                                                        Replace
                                                    </ActionLink>
                                                </li>
                                            )}
                                            {canSimplify && (
                                                <li>
                                                    <ActionLink id={`simplify-analyses-${budget.id}`} className="IconLink--group" onClick={_simplifyAnalysis}>
                                                        Simplify
                                                    </ActionLink>
                                                </li>
                                            )}
                                            {canCompare && (
                                                <li>
                                                    <ActionLink id={`compare-analyses-${budget.id}`} className="IconLink--compare" onClick={_compareAnalyses}>
                                                        Compare
                                                    </ActionLink>
                                                </li>
                                            )}
                                            {false && (
                                                <li>
                                                    <Link id={`plan-analyses-${budget.id}`} className="IconLink--stats-dots" to={`/app/farm/${farm.id}/analysis/${budget.id}/summary`}>
                                                        Plan & compare
                                                    </Link>
                                                </li>
                                            )}
                                            {canDownload && (
                                                <li>
                                                    <ActionLink id={`download-analysis-${budget.id}`} className="IconLink--shield u-link" onClick={() => downloadBudget(farm.id, budget.id)}>
                                                        Download
                                                    </ActionLink>
                                                </li>
                                            )}
                                        </ul>
                                    )}
                                </CardActionsRight>
                            </CardActions>
                            {hasBodyContent && (
                                <CardBody collapsed={collapsed} className="u-ml-sm">
                                    {publications.length > 0 &&
                                        publications
                                            .filter((p) => p.workingCopyId)
                                            .map((publication) => {
                                                const offlineWorkingCopy = farm.offlineBudgets.find((ob) => ob.id === publication.workingCopyId);
                                                return offlineWorkingCopy ? (
                                                    <Panel key={offlineWorkingCopy.id} title={`Working copy - ${publication.publishedTo}`} small midBlue className="u-mt-md">
                                                        <PanelBody grey>
                                                            <AnalysisCard key={offlineWorkingCopy.id} {...props} offlineBudget={offlineWorkingCopy} />
                                                        </PanelBody>
                                                    </Panel>
                                                ) : undefined;
                                            })}
                                    {publications.length > 0 && (
                                        <Panel title={`Publications (${publications.length})`} small skyBlue className="u-mt-md">
                                            <PanelBody grey>
                                                {publications.map((publication) => (
                                                    <PublicationCard budget={budget} key={publication.id} online={online} farm={farm} publication={publication} />
                                                ))}
                                            </PanelBody>
                                        </Panel>
                                    )}
                                </CardBody>
                            )}
                        </>
                    );
                }}
            </Card>
            {renameAnalysisModal && <RenameAnalysisModal {...renameAnalysisModal.modalProps} />}
            {analysisAuditLogModal && <AnalysisAuditLogModal {...analysisAuditLogModal.modalProps} />}
            {createAnalysisModal && <CreateAnalysisModal {...createAnalysisModal.modalProps} />}
            {replaceAnalysisModal && <ReplaceAnalysisModal {...replaceAnalysisModal.modalProps} />}
            {simplifyAnalysisModal && <SimplifyAnalysisModal {...simplifyAnalysisModal.modalProps} />}
            {compareAnalysesModal && <CompareAnalysesModal {...compareAnalysesModal.modalProps} />}
            {analysisCommentsModal}
        </>
    );
};

export default compose(withAppContext, withActions({ push, reduxModal, confirm, grantBudgetAccess, addBudgetToStudentGroup, deleteBudget, downloadBudget, getStudents, getAccount }))(AnalysisCard);
